import { Injectable, inject } from '@angular/core';
import { collection, addDoc, collectionData, collectionSnapshots, doc, deleteDoc, setDoc, Firestore, type DocumentSnapshot, type DocumentData} from '@angular/fire/firestore';
import { type Observable, BehaviorSubject } from 'rxjs';
import type {OrderDetail} from '../../features/ordering/shopping-cart.model';
import { HttpClient } from '@angular/common/http';
import type {ContactUs} from '../../models/contact-us.model';
import type {TableBooking} from '../../models/table-booking.model';
import type {CateringMenu, MenuItem} from '../../models/menu-item.model';
import type {User} from '../../models/user.model';
import type {TableBookingSlot} from '../../models/table-booking-slot.model';


@Injectable({
    providedIn: 'root'
})
export class FirebaseService {
    db = inject(Firestore);
    private http = inject(HttpClient);

    private orderInfo: BehaviorSubject<unknown>;
    private viewportChanged: BehaviorSubject<string | null>;
    private floatButton: BehaviorSubject<boolean>;
    private allMenuItems: Observable<DocumentSnapshot<DocumentData>[]> = null;
    private openingHours: Observable<DocumentSnapshot<DocumentData>[]> = null;
    private bannerText: Observable<DocumentSnapshot<DocumentData>[]> = null;
    private cateringMenuItems: Observable<DocumentSnapshot<DocumentData>[]> = null;

    constructor() {
        this.orderInfo = new BehaviorSubject<unknown>(null);
        this.viewportChanged = new BehaviorSubject<string | null>(null);
        this.floatButton = new BehaviorSubject<boolean>(false);
    }

    addContactUs(item: ContactUs) {
        return addDoc(collection(this.db, 'contactus'), {
            name: item.name,
            email: item.email,
            phone: item.message,
            message: item.message,
            contactId: item.contactId || 1
        });
    }

    addTableBooking(item: TableBooking) {
        return addDoc(collection(this.db, 'tablebooking'), {
            name: item.name,
            email: item.email,
            phone: item.phone,
            message: item.message,
            numPeople: item.numPeople,
            date: item.date,
            slot: item.slot,
            bookingId: item.bookingId || 1,
            slotId: item.slotId
        });
    }

    getTableBookingSlots() {
        return collectionSnapshots(collection(this.db, 'tableBookingSlots'));
    }

    getTableBookingSlotValueChanges() {
        return collectionData(collection(this.db, 'tableBookingSlots'));
    }

    addTableBookingSlot(item: TableBookingSlot) {
        return addDoc(collection(this.db, 'tableBookingSlots'), {
            id: item.id,
            value: item.value,
            numPeople: item.numPeople
        });
    }

    deleteTableBookingSlot(itemKey: string) {
        return deleteDoc(doc(this.db, 'tableBookingSlots', itemKey));
    }

    getPreviousTableBookings() {
        return collectionData(collection(this.db, 'tablebooking'));
    }

    getAllContactUsItems() {
        return collectionData(collection(this.db, 'contactus'));
    }

    getAllOrders() {
        return collectionData(collection(this.db, 'orders'));
    }

    getAllUsers() {
        return collectionData(collection(this.db, 'users'));
    }

    registerUser(user: User) {
        return addDoc(collection(this.db, 'users'), {
            id: user.id,
            type: 'member',
            username: user.username,
            password: user.password,
            firstname: user.firstName,
            lastname: user.lastName
        });
    }

    setFloatButton(v: boolean): void {
        this.floatButton.next(v);
    }

    getFloatButton(): Observable<boolean> {
        return this.floatButton.asObservable();
    }

    getValue(): Observable<unknown> {
        return this.orderInfo.asObservable();
    }

    setViewPort(newValue: string | null): void {
        this.viewportChanged.next(newValue);
    }

    getViewPort(): Observable<string | null> {
        return this.viewportChanged.asObservable();
    }

    setValue(newValue: unknown): void {
        this.orderInfo.next(newValue);
    }

    getAllMenuItems() {
        if (!this.allMenuItems) {
            this.allMenuItems = collectionSnapshots(collection(this.db, 'allMenuItems'));
        }
        return this.allMenuItems;
    }

    getOpeningHours() {
        if (!this.openingHours) {
            this.openingHours = collectionSnapshots(collection(this.db, 'openingHours'));
        }
        return this.openingHours;
    }

    getBannerText() {
        if (!this.bannerText) {
            this.bannerText = collectionSnapshots(collection(this.db, 'topBannerTexts'));
        }
        return this.bannerText;
    }

    getAllCategories() {
        return collectionSnapshots(collection(this.db, 'menuCategory'));
    }

    addMenu(item: MenuItem) {
        return addDoc(collection(this.db, 'allMenuItems'), {
            title: item.title,
            section: item.section,
            price: item.price,
            qty: item.qty,
            isVisible: item.isVisible,
            showDescription: item.showDescription,
            description: item.description
        });
    }

    addCateringMenu(item: CateringMenu) {
        return addDoc(collection(this.db, 'cateringMenuItems'), {
            title: item.title,
            items: item.items,
            price: item.price,
            qty: item.qty,
            isVisible: item.isVisible,
            showDescription: item.showDescription,
            description: item.description
        });
    }

    updateCateringMenu(menuKey: string, item: CateringMenu) {
        return setDoc(doc(this.db, 'cateringMenuItems', menuKey), {
            title: item.title,
            items: item.items,
            price: item.price,
            qty: item.qty,
            isVisible: item.isVisible,
            showDescription: item.showDescription,
            description: item.description
        });
    }

    getCateringMenuItems() {
        if (!this.cateringMenuItems) {
            this.cateringMenuItems = collectionSnapshots(collection(this.db, 'cateringMenuItems'));
        }
        return this.cateringMenuItems;
    }

    placeOrder(item: OrderDetail) {
        return addDoc(collection(this.db, 'orders'), {
            id: item.id,
            customerName: item.customerName,
            customerAddress: item.customerAddress || '',
            customerPhone: item.customerPhone,
            email: item.email,
            details: item.details,
            paymentMethod: item.paymentMethod,
            deliveryOption: item.deliveryOption,
            subject: item.subject
        });
    }

    deleteMenuItem(menuKey: string) {
        return deleteDoc(doc(this.db, 'allMenuItems', menuKey));
    }

    deleteCateringMenuItem(menuKey: string) {
        return deleteDoc(doc(this.db, 'cateringMenuItems', menuKey));
    }

    updateMenuItem(menuKey: string, item: MenuItem) {
        return setDoc(doc(this.db, 'allMenuItems', menuKey), {
            title: item.title,
            section: item.section,
            price: item.price,
            qty: item.qty,
            isVisible: item.isVisible,
            showDescription: item.showDescription,
            description: item.description
        });
    }

}
